'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Copy, ShareNetwork } from '@phosphor-icons/react';
import React, { useState } from 'react';
import {
  BlueskyIcon,
  BlueskyShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';

export interface ShareJobDialogProps {
  share_link: string;
  share_media: string;
  share_title: string;
}

export function ShareJobDialog({
  share_link,

  share_title,
}: ShareJobDialogProps) {
  const [copyStatus, setCopyStatus] = useState('Copy to clipboard');

  const copyURLToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus('Copy to clipboard'), 4000);
    } catch (err) {
      console.error('Failed to copy job: ', err);
      setCopyStatus('Failed to copy');
    }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="group flex w-full place-content-center items-center space-x-3 shadow-none"
        >
          <span className="text-normal font-semibold tracking-wide">
            Share this job
          </span>
          <ShareNetwork
            className="group-hover:motion-rotate-in-[0.5turn]"
            size={20}
          />
        </Button>
      </DialogTrigger>
      <DialogContent className="shodow-none border-secondary sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Share job</DialogTitle>
          <DialogDescription className="space-y-6 pt-3">
            <div>
              <Button
                data-umami-event="job copied to clipboard"
                onClick={copyURLToClipboard}
                className="group flex w-full place-content-center items-center space-x-3 shadow-none"
              >
                <span className="text-normal font-semibold">{copyStatus}</span>
                <Copy
                  weight="duotone"
                  className="group-hover:motion-preset-expand"
                />
              </Button>
            </div>
            <div className="flex place-content-center items-center justify-between">
              <FacebookShareButton
                data-umami-event="job shared on facebook"
                title={share_title}
                url={share_link}
                hashtag="#creativefuego #jobs #hiring #jobsearch"
              >
                <FacebookIcon size={40} className="rounded-md" />
              </FacebookShareButton>
              <TwitterShareButton
                data-umami-event="job shared on twitter"
                url={share_link}
                title={share_title}
              >
                <TwitterIcon size={40} className="rounded-md" />
              </TwitterShareButton>

              <BlueskyShareButton
                data-umami-event="job shared on bluesky"
                url={share_link}
                title={share_title}
              >
                <BlueskyIcon size={40} className="rounded-md" />
              </BlueskyShareButton>
              <RedditShareButton
                data-umami-event="job shared on reddit"
                url={share_link}
                title={share_title}
              >
                <RedditIcon size={40} className="rounded-md" />
              </RedditShareButton>
              <LinkedinShareButton
                data-umami-event="job shared on LinkedIn"
                url={share_link}
                title={share_title}
                source="https://creativefuego.com"
              >
                <LinkedinIcon size={40} className="rounded-md" />
              </LinkedinShareButton>
              <TelegramShareButton
                data-umami-event="job shared on telegram"
                url={share_link}
                title={share_title}
              >
                <TelegramIcon size={40} className="rounded-md" />
              </TelegramShareButton>
              <WhatsappShareButton
                data-umami-event="job shared on whatsapp"
                url={share_link}
                title={share_title}
              >
                <WhatsappIcon size={40} className="rounded-md" />
              </WhatsappShareButton>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
